import React, { useEffect, useState } from "react";
import "./styles/ApiSecurityPanel.css";
import { GetApiKeyResponse, ManageApiService } from "../services/ManageApiService";
import WebhooksPanel from "./WebhooksPanel";

const ApiSecurityPanel: React.FC = () => {
  const [apiKeys, setApiKeys] = useState<GetApiKeyResponse[]>([]);
  const [newKeyName, setNewKeyName] = useState("");
  const [newKeyFullValue, setNewKeyFullValue] = useState<string | null>(null);

  const [toast, setToast] = useState<string | null>(null);

  const showToast = (message: string) => {
    setToast(message);
    setTimeout(() => {
      setToast(null);
    }, 2500);
  };

  useEffect(() => {
    const fetchKeys = async () => {
      const response = await ManageApiService.getApiKeys();

      if (response.error) {
        return;
      }

      setApiKeys(response.data?.apiKeys ?? []);
    };

    fetchKeys();
  }, []);

  const handleCopy = async (value: string, label = "Value") => {
    try {
      await navigator.clipboard.writeText(value);
      showToast(`${label} copied to clipboard`);
    } catch {
      showToast(`Failed to copy ${label.toLowerCase()}`);
    }
  };

  const handleGenerateKey = async () => {
    if (!newKeyName.trim()) {
      showToast("Please enter a name for the API key.");
      return;
    }

    const response = await ManageApiService.generateApiKey(newKeyName);

    if (response.error) {
      showToast("Failed to generate API key.");
      return;
    }

    const fullKey = response.data?.apiKey ?? "";

    const truncated = fullKey.slice(0, 12) + "****";
    const now = new Date().toISOString().slice(0, 10);

    const newKey: GetApiKeyResponse = {
      id: String(Date.now()),
      name: newKeyName.trim(),
      createdAt: now,
      lastUsedAt: now,
      prefix: truncated,
      isActive: true,
    };

    setApiKeys((prev) => [newKey, ...prev]);
    setNewKeyFullValue(fullKey);
    setNewKeyName("");
    showToast("New API key generated. Store it somewhere safe.");
  };

  const handleRevokeKey = (id: string) => {
    const key = apiKeys.find((k) => k.id === id);
    const confirmed = window.confirm(
      `Are you sure you want to revoke "${key?.name}"? This action cannot be undone.`
    );
    if (!confirmed) return;

    setApiKeys((prev) =>
      prev.map((k) => (k.id === id ? { ...k, isActive: false } : k))
    );
    showToast("API key revoked.");
  };

  return (
    <div className="api-security-container">
      <header className="api-security-header">
        <div>
          <h1 className="api-security-title">API Access & Webhooks</h1>
          <p className="api-security-subtitle">
            Manage your API keys and webhooks. For security reasons, full
            secrets are only shown once—copy and store them safely.
          </p>
        </div>
      </header>

      {/* New API key form */}
      <section className="api-card">
        <div className="api-card-header">
          <div>
            <h2 className="api-card-title">Create new API key</h2>
            <p className="api-card-subtitle">
              Use separate keys per environment or service. Revoke keys you no
              longer need.
            </p>
          </div>
        </div>

        <div className="api-form-grid">
          <div className="api-form-field">
            <label className="api-label" htmlFor="keyName">
              Key name
            </label>
            <input
              id="keyName"
              className="api-input"
              placeholder="e.g. Backend service (production)"
              value={newKeyName}
              onChange={(e) => setNewKeyName(e.target.value)}
            />
          </div>
        </div>

        <div className="api-actions-row">
          <button
            type="button"
            className="api-primary-button"
            onClick={handleGenerateKey}
          >
            Generate API key
          </button>
          <p className="api-warning-text">
            You’ll only see the full key once. After that, only a shortened
            version is visible.
          </p>
        </div>

        {newKeyFullValue && (
          <div className="api-secret-banner">
            <div>
              <p className="api-secret-label">New API key (copy now)</p>
              <code className="api-secret-value">{newKeyFullValue}</code>
            </div>
            <button
              type="button"
              className="api-ghost-button"
              onClick={() => handleCopy(newKeyFullValue, "API key")}
            >
              Copy
            </button>
          </div>
        )}
      </section>

      {/* Existing keys list */}
      <section className="api-card">
        <div className="api-card-header">
          <div>
            <h2 className="api-card-title">Existing API keys</h2>
            <p className="api-card-subtitle">
              Rotate keys regularly and revoke keys that are not in use.
            </p>
          </div>
        </div>

        {apiKeys.length === 0 ? (
          <p className="api-empty-state">
            You haven’t created any API keys yet.
          </p>
        ) : (
          <div className="api-keys-table" role="table" aria-label="API keys">
            <div className="api-keys-header-row" role="row">
              <div className="api-keys-header-cell" role="columnheader">
                Name
              </div>
              <div className="api-keys-header-cell" role="columnheader">
                Key
              </div>
              <div className="api-keys-header-cell" role="columnheader">
                Created
              </div>
              <div className="api-keys-header-cell" role="columnheader">
                Last used
              </div>
              <div className="api-keys-header-cell" role="columnheader">
                Status
              </div>
              <div className="api-keys-header-cell" role="columnheader">
                Actions
              </div>
            </div>

            {apiKeys.map((key) => (
              <div className="api-keys-row" role="row" key={key.id}>
                <div className="api-keys-cell" role="cell">
                  <span className="api-key-name">{key.name}</span>
                </div>
                <div className="api-keys-cell" role="cell">
                  <code className="api-key-truncated">
                    {key.prefix + "****"}
                  </code>
                </div>
                <div className="api-keys-cell" role="cell">
                  {key.createdAt}
                </div>
                <div className="api-keys-cell" role="cell">
                  {key.lastUsedAt ?? "—"}
                </div>
                <div className="api-keys-cell" role="cell">
                  <span
                    className={`api-status-pill api-status-pill--${
                      key.isActive ? "active" : "revoked"
                    }`}
                  >
                    {key.isActive ? "Active" : "Revoked"}
                  </span>
                </div>
                <div className="api-keys-cell api-keys-actions" role="cell">
                  <button
                    type="button"
                    className="api-ghost-button api-keys-action-button"
                    onClick={() =>
                      showToast(
                        "Full keys are only visible at creation. Use a new key if needed."
                      )
                    }
                  >
                    Details
                  </button>
                  {key.isActive && (
                    <button
                      type="button"
                      className="api-danger-button api-keys-action-button"
                      onClick={() => handleRevokeKey(key.id)}
                    >
                      Revoke
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Webhooks panel (extracted component) */}
      <WebhooksPanel showToast={showToast} onCopy={handleCopy} />

      {/* Toast */}
      <div
        className={`api-toast ${toast ? "api-toast--visible" : ""}`}
        aria-live="polite"
      >
        {toast}
      </div>
    </div>
  );
};

export default ApiSecurityPanel;
